/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState, useEffect, useRef} from 'react'
import db from '../../../services/firebase'
import {collection, doc, onSnapshot, updateDoc} from 'firebase/firestore'
import ReactPaginate from 'react-paginate'
import './pagination.css'
import {Helmet} from 'react-helmet'
import {TestListLoading} from './components/loading/TestListLoading'
import {useAuth} from '../../modules/auth'
import {useLocation} from 'react-router-dom'
import {TestListToolbar} from './components/header/TestListToolbar'
import {Overlay, Popover} from 'react-bootstrap'
import useCrispChat from 'app/Hooks/useCrispChat'
import ViewTests from './components/ViewTests'
import Searchbar from 'app/components/Searchbar'
import Button from 'app/components/Button'
import useAuditContext from 'app/Hooks/useAuditContext'
import AssignOwnerForm from 'app/components/AssignOwnerForm'
import {TestStatusBadge} from './components/viewTestsComponents'
import OwnerDisplay from 'app/components/OwnerDisplay'

type Props = {
  className: string
  tests: any[]
  status: string
  audit?: any
}

const TestManagement: React.FC<Props> = ({tests, status, audit}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [testID, setTestID] = useState('')
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [searchLength, setSearchLength] = useState(false)
  const {currentUser} = useAuth()
  const [selectedNumber, setSelectedNumber] = useState('50')
  let ITEMS_PER_PAGE = parseInt(selectedNumber)
  const [checkedItems, setCheckedItems] = useState<any[]>([])
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const [selectAll, setSelectAll] = useState<boolean>(false)
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get('id')
  const [show1, setShow1] = useState(false)
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const overlayRef = useRef<HTMLDivElement>(null)
  const [personnels, setPersonnels] = useState<any[]>([])
  const target = useRef(null)
  const {toggleCrispChat} = useCrispChat()
  const [activeRowId, setActiveRowId] = useState('')
  const {currentAuditContext} = useAuditContext()

  useEffect(() => {
    getActiveFramworks()
  }, [])

  useEffect(() => {
    if (id) {
      handleOpenModal(id)
    }
  }, [id])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 1000)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'employees')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const risksData = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          end_date: doc.data().end_date,
        }))
        .filter((data) => !data.end_date)
      setPersonnels(risksData)
    })

    return unsubscribe
  }, [])

  const maskStringPreservingSpaces = (str, visibleStartLength = 5, visibleEndLength = 4) => {
    if (!str || str.length <= visibleStartLength + visibleEndLength) return '****'

    const start = str.slice(0, visibleStartLength)
    const end = str.slice(-visibleEndLength)
    const middle = str.slice(visibleStartLength, -visibleEndLength)

    const maskedMiddle = [...middle].map((char) => (char === ' ' ? ' ' : '*')).join('')
    return `${start}${maskedMiddle}${end}`
  }

  const getActiveFramworks = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      const testsCollectionRef = collection(tenantRef, 'active_frameworks')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }))
        const frameworks = frameworksData[0]?.frameworks || []
        setActiveFrameworks(frameworks)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting tests:', error)
    }
  }

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target as Node) && show1) {
        setShow1(false)
      }
    }

    if (show1) {
      document.addEventListener('mousedown', handleOutsideClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [show1])

  const updateTestStatus = (test: any) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (test.next_review_date) {
      const reviewDate = new Date(test.next_review_date)
      reviewDate.setHours(0, 0, 0, 0)

      if (reviewDate <= today && test.status !== 'Fail') {
        const tenantId = currentUser?.tenantId || ''
        if (tenantId) {
          const tenantRef = doc(db, 'tenants', tenantId)
          const testRef = doc(tenantRef, 'tests', test.id)
          updateDoc(testRef, {status: 'Fail'})
        }
        return {...test, status: 'Fail'}
      }
    }
    return test
  }

  useEffect(() => {
    if (tests.length > 0) {
      let baseItems = tests
        .map(updateTestStatus)
        .filter((test) =>
          status === 'Deactivated' ? test.status === 'Deactivated' : test.status !== 'Deactivated'
        )

      if (
        currentAuditContext &&
        currentAuditContext.framework &&
        currentAuditContext.framework.length > 0
      ) {
        baseItems = baseItems.filter((test) => {
          const testFrameworks = test.framework.split(',').map((fw) => fw.trim())
          return currentAuditContext.framework.some((auditFramework) =>
            testFrameworks.includes(auditFramework)
          )
        })
      }

      baseItems = baseItems.sort((a, b) => {
        const policyA = a.name || ''
        const policyB = b.name || ''
        return policyA.localeCompare(policyB)
      })

      if (searchTerm !== '') {
        if (currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus') {
          baseItems = baseItems
            .slice(0, 5)
            .filter((test) => test.name.toLowerCase().includes(searchTerm))
          setSearchLength(baseItems.length === 0)
        } else {
          baseItems = baseItems.filter((test) => test.name.toLowerCase().includes(searchTerm))
          setSearchLength(baseItems.length === 0)
        }
      } else {
        setSearchLength(false)
      }

      if (currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus') {
        baseItems = baseItems.map((test, index) => {
          if (index >= 5) {
            return {
              ...test,
              name: maskStringPreservingSpaces(test.name),
              framework: '******',
              category: '****',
              timeframe: '*****',
              owner: '',
              next_review_date: '',
            }
          } else {
            return test
          }
        })
      }

      setFilteredItems(baseItems)
    }
  }, [searchTerm, currentUser, status, tests, audit, currentAuditContext])
  
  const pageCount = Math.ceil(filteredItems.length / ITEMS_PER_PAGE)

  const handlePageClick = ({selected}: {selected: number}) => {
    setCurrentPage(selected)
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  const offset = currentPage * ITEMS_PER_PAGE

  const paginatedData = filteredItems.slice(offset, offset + ITEMS_PER_PAGE)

  const handleOpenModal = (id) => {
    setIsModalOpen(true)
    setTestID(id)
    setActiveRowId(id)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setActiveRowId('')
  }

  const handleDeselectClick = () => {
    setCheckedItems([])
    setSelectAll(false)
  }

  const handleSelectAllChange = (e) => {
    const isChecked = e.target.checked
    setSelectAll(isChecked)
    setCheckedItems(isChecked ? filteredItems.map((item) => item.id) : [])
  }

  const handleCheckChange = (e) => {
    const {checked, value} = e.target
    setCheckedItems((prev) => {
      const newCheckedItems = checked ? [...prev, value] : prev.filter((item) => item !== value)
      setSelectAll(newCheckedItems.length === filteredItems.length)

      return newCheckedItems
    })
  }

  function formatDate(dateString) {
    if (!dateString) return '--'

    const date = new Date(dateString)
    const day = date.getDate()
    const month = date.toLocaleString('default', {month: 'long'})
    const year = date.getFullYear()

    return `${day} ${month}, ${year}`
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | Tests</title>
      </Helmet>

      <div className='card-header border-0 pt-6 h-100 mb-1'>
        <div className='card-title d-flex justify-content-between align-items-center'>
          <div className='flex-lg-fill'>
            <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
        </div>
        <div className='card-toolbar'>
          {checkedItems.length > 0 ? (
            <>
              {checkedItems.length > 0 ? (
                <div className='border-end pe-4 text-center text-gray-900'>
                  <span>
                    <p className='pt-4'>{checkedItems.length} Selected</p>
                  </span>
                </div>
              ) : null}
              <div style={{display: 'flex', alignItems: 'center'}}>
                <div className='mx-5' style={{cursor: 'pointer'}} onClick={handleDeselectClick}>
                  <img src='/media/buttons/Deselect.svg' alt='Deselect' />
                </div>

                <div className='mb-1' style={{cursor: 'pointer'}} ref={target}>
                  <Button
                    btnType='primary'
                    onClick={() => setShow1(!show1)}
                    disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                  >
                    Assign Owner
                  </Button>
                </div>
                <Overlay
                  show={show1}
                  target={target.current}
                  placement='bottom'
                  container={document.body}
                  containerPadding={10}
                >
                  {(props) => (
                    <Popover id='popover-contained' {...props}>
                      <div ref={overlayRef as React.RefObject<HTMLDivElement>}>
                        <Popover.Body onClick={(e) => e.stopPropagation()}>
                          <AssignOwnerForm
                            personnels={personnels}
                            checkedItems={checkedItems}
                            onClose={() => setShow1(false)}
                            onSuccess={() => {
                              setCheckedItems([])
                              setSelectAll(false)
                            }}
                            currentUser={currentUser}
                            collectionName='tests'
                            fieldName='owner'
                          />
                        </Popover.Body>
                      </div>
                    </Popover>
                  )}
                </Overlay>
              </div>
            </>
          ) : (
            <>
              <TestListToolbar />
            </>
          )}
        </div>
      </div>

      <div className='card-body py-0'>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 table-hover'>
            <thead>
              <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                <th className='w-25px'>
                  {currentUser?.account_type !== 'Trial' && currentUser?.account_type !== 'Trial-plus' && (
                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={selectAll}
                        data-kt-check='true'
                        data-kt-check-target='.widget-9-check'
                        onChange={handleSelectAllChange}
                      />
                    </div>
                  )}
                </th>

                <th className='min-w-300px'>NAME</th>
                <th className='min-w-50px'>OWNER</th>
                {/* <th className='min-w-100px'>STATUS</th> */}
                <th className='min-w-100px'>TYPE</th>
                <th className='min-w-100px'>CATEGORY</th>
                <th className='min-w-100px'>NEXT REVIEW</th>
                <th className='min-w-50px'>TEST INTERVAL</th>
                {/* <th className='min-w-100px'>STATUS</th> */}
              </tr>
            </thead>
            <ViewTests isOpen={isModalOpen} onClose={handleCloseModal} id={testID} />

            {paginatedData && paginatedData.length > 0 ? (
              paginatedData.map((testsData, index) => {
                if ((currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus') && index < 5) {
                  return (
                    <tbody key={`tbody-${testsData.id}-${index}`}>
                      <tr
                        key={testsData.id}
                        className={`${activeRowId === testsData.id ? 'bg-gray-100' : ''}`}
                      >
                        <td>
                          <div className='form-check form-check-sm form-check-custom form-check-solid'>
                            <input
                              className='form-check-input widget-9-check'
                              type='checkbox'
                              value={testsData.id}
                              checked={selectAll || checkedItems.includes(testsData.id)}
                              onChange={handleCheckChange}
                            />
                          </div>
                        </td>

                        <td>
                          <div className='d-flex flex-column'>
                            <a
                              href='#'
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleOpenModal(testsData.id)
                              }}
                              className='text-dark fw-bold text-hover-primary d-block fs-6 mb-1'
                            >
                              {' '}
                              {testsData.name}
                            </a>
                            <span>
                              <span
                                className={`badge fs-7 ${
                                  testsData.status === 'Deactivated'
                                    ? 'text-dark text-bold m-1'
                                    : ''
                                }`}
                                style={{
                                  color:
                                    testsData.status === 'Deactivated'
                                      ? '#5A5A5A'
                                      : testsData.status === 'Pass'
                                      ? '#0E9F6E'
                                      : '#EE3043',
                                  background:
                                    testsData.status === 'Deactivated'
                                      ? '#F6F6F6'
                                      : testsData.status === 'Pass'
                                      ? '#E8F8F4'
                                      : '#FFF5F8',
                                }}
                              >
                                {' '}
                                {testsData.status === 'Deactivated'
                                  ? 'Deactivated'
                                  : testsData.status === 'Pass'
                                  ? 'Passing'
                                  : 'Failing'}
                              </span>

                              <span className='fs-7 fw-semibold mx-2'>
                                {testsData.framework
                                  .split(', ')
                                  .filter((framework) => {
                                    if (currentAuditContext && currentAuditContext.framework) {
                                      return currentAuditContext.framework.includes(
                                        framework.trim()
                                      )
                                    }
                                    return activeFrameworks.some((item) =>
                                      framework.startsWith(item)
                                    )
                                  })
                                  .map((framework, index, array) => (
                                    <React.Fragment key={index}>
                                      {framework}
                                      {index < array.length - 1 && ', '}
                                    </React.Fragment>
                                  ))}
                              </span>
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex align-items-center'>
                            {testsData.owner ? (
                              <>
                                <div className='symbol symbol-25px me-5'>
                                  {testsData.owner ? (
                                    testsData.owner_uid ? (
                                      <OwnerDisplay owner={testsData.owner} uid={testsData.owner_uid} firstNameOnly={true} />
                                    ) : (
                                      <div className='symbol symbol-25px'>
                                        <span
                                          className='symbol-label fs-7 fw-bold'
                                          style={{
                                            backgroundColor: '#F5F5F5',
                                            border: '1px solid #E5E5E5',
                                          }}
                                        >
                                          {testsData.owner
                                            ? (() => {
                                                const nameParts = testsData.owner.split(' ')
                                                if (nameParts.length > 1) {
                                                  return `${nameParts[0].charAt(0).toUpperCase()}`
                                                } else {
                                                  return `${nameParts[0].charAt(0).toUpperCase()}`
                                                }
                                              })()
                                            : ''}
                                        </span>
                                      </div>
                                    )
                                  ) : (
                                    <>-</>
                                  )}
                                </div>
                              </>
                            ) : (
                              <>--</>
                            )}
                          </div>
                        </td>
                        <td>
                          <a
                            href='#'
                            className='badge fs-7 text-dark text-bold m-1'
                            style={{background: '#F6F6F6'}}
                          >
                            {testsData.type}
                          </a>
                        </td>

                        <td>
                          <div className='fs-6 fw-semibold'>{testsData.category}</div>
                        </td>
                        <td>
                          {' '}
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2 fw-semibold fs-6'>
                              {testsData.next_review_date ? (
                                formatDate(testsData.next_review_date)
                              ) : (
                                <>--</>
                              )}
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2'>
                              <a href='#' className='text-dark fw-semibold ml-5 fs-6'>
                                {testsData.timeframe ? testsData.timeframe : '--'}
                              </a>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  )
                } else if ((currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus') && index === 5) {
                  return (
                    <tbody key='message-row' className=''>
                      <tr>
                        <td colSpan={100}>
                          <div className='d-flex align-items-center justify-content-center gap-3 fw-semibold text-capitalize'>
                            <span>To unlock full access and view all data, please upgrade your plan.</span>
                            <button
                              className='btn btn-sm fs-6'
                              style={{background: '#0E9F6E', color: '#fff'}}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor = '#148363')
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor = '#0E9F6E')
                              }
                              onClick={toggleCrispChat}
                            >
                              Contact Sales
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  )
                } else if ((currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus') && index > 5) {
                  return (
                    <tbody key={`tbody-${testsData.id}-${index}`}>
                      <tr
                        key={testsData.id}
                        className={`${activeRowId === testsData.id ? 'bg-gray-100' : ''}`}
                        style={{filter: 'blur(4px)', pointerEvents: 'none'}}
                      >
                        <td>
                          <div className='form-check form-check-sm form-check-custom form-check-solid'>
                            <input
                              className='form-check-input widget-9-check'
                              type='checkbox'
                              value={testsData.id}
                              checked={selectAll || checkedItems.includes(testsData.id)}
                              onChange={handleCheckChange}
                            />
                          </div>
                        </td>

                        <td>
                          <div className='d-flex flex-column'>
                            <a
                              href='#'
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                              }}
                              className='text-dark fw-bold text-hover-primary d-block fs-6 mb-1'
                            >
                              {' '}
                              {testsData.name}
                            </a>
                            <span>
                              <span
                                className={`badge fs-7 text-dark text-bold m-1`}
                                style={{
                                  color: '#5A5A5A',
                                  background: '#F6F6F6',
                                }}
                              >
                                {' '}
                                ****
                              </span>

                              <span className='fs-7 fw-semibold mx-2'>
                                {testsData.framework
                                  .split(', ')
                                  .filter((framework) => {
                                    if (currentAuditContext && currentAuditContext.framework) {
                                      return currentAuditContext.framework.includes(
                                        framework.trim()
                                      )
                                    }
                                    return activeFrameworks.some((item) =>
                                      framework.startsWith(item)
                                    )
                                  })
                                  .map((framework, index, array) => (
                                    <React.Fragment key={index}>
                                      {framework}
                                      {index < array.length - 1 && ', '}
                                    </React.Fragment>
                                  ))}
                              </span>
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex align-items-center'>
                            {testsData.owner ? (
                              <>
                                <div className='symbol symbol-25px me-5'>
                                  <div className='symbol symbol-25px'>
                                    <span
                                      className='symbol-label fs-7 fw-bold'
                                      style={{
                                        backgroundColor: '#F5F5F5',
                                        border: '1px solid #E5E5E5',
                                      }}
                                    >
                                      **
                                    </span>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>--</>
                            )}
                          </div>
                        </td>

                        <td>
                          <a
                            href='#'
                            className='badge fs-7 text-dark text-bold m-1'
                            style={{background: '#F6F6F6'}}
                          >
                            ****
                          </a>
                        </td>

                        <td>
                          <div className='fs-6 fw-semibold'>{testsData.category}</div>
                        </td>
                        <td>
                          {' '}
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2 fw-semibold fs-6'>
                              {testsData.next_review_date ? '**-**-****' : <>--</>}
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2'>
                              <a href='#' className='text-dark fw-semibold ml-5 fs-6'>
                                {testsData.timeframe ? testsData.timeframe : '--'}
                              </a>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  )
                } else {
                  return (
                    <tbody key={`tbody-${testsData.id}-${index}`}>
                      <tr
                        key={testsData.id}
                        className={`${activeRowId === testsData.id ? 'bg-gray-100' : ''}`}
                      >
                        <td>
                          <div className='form-check form-check-sm form-check-custom form-check-solid'>
                            <input
                              className='form-check-input widget-9-check'
                              type='checkbox'
                              value={testsData.id}
                              checked={selectAll || checkedItems.includes(testsData.id)}
                              onChange={handleCheckChange}
                            />
                          </div>
                        </td>

                        <td>
                          <div className='d-flex flex-column'>
                            <a
                              href='#'
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleOpenModal(testsData.id)
                              }}
                              className='text-dark fw-bold text-hover-primary d-block fs-6 mb-1'
                            >
                              {' '}
                              {testsData.name}
                            </a>
                            {currentUser?.role !== 'Auditor' ? (
                              <span>
                                {!currentAuditContext ? (
                                  <span className='fw-normal fs-6'>
                                    <TestStatusBadge status={testsData.status} isAuditor={false} />
                                  </span>
                                ) : (
                                  <span className='fw-normal fs-6'>
                                    <TestStatusBadge
                                      status={testsData.status}
                                      auditorReview={testsData.auditor_review}
                                      isAuditor={true}
                                      auditId={audit?.id}
                                    />
                                  </span>
                                )}
                                <span className='fs-7 fw-semibold mx-2'>
                                  {testsData.framework
                                    .split(', ')
                                    .filter((framework) => {
                                      if (currentAuditContext && currentAuditContext.framework) {
                                        return currentAuditContext.framework.includes(
                                          framework.trim()
                                        )
                                      }
                                      return activeFrameworks.some((item) =>
                                        framework.startsWith(item)
                                      )
                                    })
                                    .map((framework, index, array) => (
                                      <React.Fragment key={index}>
                                        {framework}
                                        {index < array.length - 1 && ', '}
                                      </React.Fragment>
                                    ))}
                                </span>
                              </span>
                            ) : (
                              <span>
                                <span className='fw-normal fs-6'>
                                  <TestStatusBadge 
                                    status={testsData.status} 
                                    auditorReview={testsData.auditor_review} 
                                    auditId={audit?.id}
                                    isAuditor={true} 
                                  />
                                </span>
                                {currentUser?.role === 'Auditor' ? (
                                  <span className='fs-7 fw-semibold mx-2'>
                                    {testsData.framework
                                      .split(', ')
                                      .filter((framework) =>
                                        audit?.framework?.some((item) => framework.startsWith(item))
                                      )
                                      .map((framework, index, array) => (
                                        <React.Fragment key={index}>
                                          {framework}
                                          {index < array.length - 1 && ', '}
                                        </React.Fragment>
                                      ))}
                                  </span>
                                ) : (
                                  <span className='fs-7 fw-semibold mx-2'>
                                    {testsData.framework
                                      .split(', ')
                                      .filter((framework) => {
                                        if (currentAuditContext && currentAuditContext.framework) {
                                          return currentAuditContext.framework.includes(
                                            framework.trim()
                                          )
                                        }
                                        return activeFrameworks.some((item) =>
                                          framework.startsWith(item)
                                        )
                                      })
                                      .map((framework, index, array) => (
                                        <React.Fragment key={index}>
                                          {framework}
                                          {index < array.length - 1 && ', '}
                                        </React.Fragment>
                                      ))}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className='d-flex align-items-center'>
                            {testsData.owner ? (
                              <>
                                <div className='symbol symbol-25px me-5'>
                                  {testsData.owner ? (
                                    testsData.owner_uid ? (
                                      <OwnerDisplay owner={testsData.owner} uid={testsData.owner_uid} firstNameOnly={true} />
                                    ) : (
                                      <div className='symbol symbol-25px'>
                                        <span
                                          className='symbol-label fs-7 fw-bold'
                                          style={{
                                            backgroundColor: '#F5F5F5',
                                            border: '1px solid #E5E5E5',
                                          }}
                                        >
                                          {testsData.owner
                                            ? (() => {
                                                const nameParts = testsData.owner.split(' ')
                                                if (nameParts.length > 1) {
                                                  return `${nameParts[0].charAt(0).toUpperCase()}`
                                                } else {
                                                  return `${nameParts[0].charAt(0).toUpperCase()}`
                                                }
                                              })()
                                            : ''}
                                        </span>
                                      </div>
                                    )
                                  ) : (
                                    <>-</>
                                  )}
                                </div>
                              </>
                            ) : (
                              <>--</>
                            )}
                          </div>
                        </td>

                        <td>
                          <a
                            href='#'
                            className='badge fs-7 text-dark text-bold m-1'
                            style={{background: '#F6F6F6'}}
                          >
                            {testsData.type}
                          </a>
                        </td>

                        <td>
                          <div className='fs-6 fw-semibold'>{testsData.category}</div>
                        </td>
                        <td>
                          {' '}
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2 fw-semibold fs-6'>
                              {testsData.next_review_date ? (
                                formatDate(testsData.next_review_date)
                              ) : (
                                <>--</>
                              )}
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2'>
                              <a href='#' className='text-dark fw-semibold ml-5 fs-6'>
                                {testsData.timeframe ? testsData.timeframe : '--'}
                              </a>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  )
                }
              })
            ) : (
              <>
                <tbody>
                  <tr>
                    {filteredItems.length === 0 && !searchTerm ? (
                      <>
                        <td colSpan={10} className='text-center'>
                          <div className='m-20'>
                            <img src='/media/buttons/emptystate.svg' alt='' />
                            <div className='m-5 fs-5'>
                              <p style={{fontWeight: 'bold'}}>No tests found</p>
                            </div>
                            <p className=''>
                              It seems your account is not assigned to any framework. Contact Sales
                              to get access to framework.
                            </p>
                          </div>
                        </td>
                      </>
                    ) : searchLength ? (
                      <td colSpan={10} className='text-center'>
                        <div className='m-20'>
                          <img src='/media/buttons/emptystate.svg' alt='' />
                          <div className='m-5 fs-5'>
                            <p style={{fontWeight: 'bold'}}>No Tests Found</p>
                          </div>
                          <p className=''>
                            We couldn't find any tests that match your search. Maybe try a different
                            search!
                          </p>
                        </div>
                      </td>
                    ) : (
                      <td colSpan={10} className='text-center'>
                        {!showEmptyState ? (
                          // <td colSpan={10}>
                          <div
                            className='d-flex justify-content-center align-items-center text-center'
                            style={{height: '500px'}}
                          >
                            <TestListLoading />
                          </div>
                        ) : (
                          <div className='m-20'>
                            <img src='/media/buttons/emptystate.svg' alt='' />
                            <div className='m-5 fs-5'>
                              <p style={{fontWeight: 'bold'}}>No Filter Results</p>
                            </div>
                            <p>Maybe try a different filter or reset filters</p>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                </tbody>
              </>
            )}
          </table>
        </div>
      </div>

      {tests.length >= 15 && (
        <div className='d-flex justify-content-between'>
          <div
            id=''
            className='col-sm-12 col-md-5 d-flex align-items-center justify-content-center justify-content-md-start dt-toolbar'
          >
            <div>
              <select
                name='kt_permissions_table_length'
                aria-controls='kt_permissions_table'
                className='form-select  mx-8'
                id='dt-length-1'
                value={selectedNumber}
                onChange={(e) => setSelectedNumber(e.target.value)}
              >
                <option value='20'>20</option>
                <option value='50'>50</option>
                <option value='100'>100</option>
              </select>
            </div>
          </div>

          <div className='d-flex justify-content-end my-10'>
            <ReactPaginate
              previousLabel={'<'}
              nextLabel={'>'}
              breakLabel={'...'}
              breakClassName={'break-me'}
              pageCount={pageCount}
              marginPagesDisplayed={1}
              pageRangeDisplayed={1}
              onPageChange={handlePageClick}
              containerClassName={'pagination'}
              activeClassName={'active'}
            />
          </div>
        </div>
      )}
    </>
  )
}

export {TestManagement}
